import type { CategoryAttribute } from '../types/foundObject';

function extractHexColor(value: string): string | null {
  const match = value.match(/#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})\b/);
  return match ? match[0] : null;
}

export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 70;

export function sanitizeTitlePart(value: string): string {
  return value.replace(/#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})\b/g, '').replace(/\s+/g, ' ').trim();
}

export function clampAnnouncementTitle(value: string): string {
  const cleaned = sanitizeTitlePart(value);
  if (cleaned.length <= ANNOUNCEMENT_TITLE_MAX_LENGTH) {
    return cleaned;
  }
  return cleaned.slice(0, ANNOUNCEMENT_TITLE_MAX_LENGTH).trim();
}

function sortCategoryDefinitionKeys(
  defs: Record<string, CategoryAttribute>
): string[] {
  return Object.keys(defs).sort((a, b) => {
    const orderA = defs[a]?.sort_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = defs[b]?.sort_order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });
}

function formatAttributeValueForTitle(
  value: unknown,
  def?: CategoryAttribute
): string {
  if (value === null || value === undefined || value === '') return '';

  if (def?.type === 'boolean') {
    const truthy = value === true || value === 'true' || value === 1 || value === '1';
    return truthy ? 'Oui' : 'Non';
  }

  if (def?.type === 'color_select' && Array.isArray(def.options)) {
    const str = String(value).trim();
    for (const opt of def.options) {
      if (typeof opt === 'string' && opt.trim() === str) {
        return sanitizeTitlePart(opt);
      }
      if (opt && typeof opt === 'object' && 'name' in opt) {
        const row = opt as { name?: string; value?: string; hex?: string | null };
        if (row.name?.trim() === str || row.value?.trim() === str || row.hex === str) {
          const label = row.name?.trim() || row.value?.trim() || '';
          if (label && !extractHexColor(label)) return sanitizeTitlePart(label);
        }
      }
    }
  }

  if (typeof value === 'string') {
    const cleaned = sanitizeTitlePart(value);
    if (def?.type === 'color_select' && extractHexColor(cleaned)) return '';
    return cleaned;
  }

  return sanitizeTitlePart(String(value));
}

export function collectTitleAttributeParts(
  objectAttributes: Record<string, unknown>,
  categoryAttributes?: Record<string, CategoryAttribute> | null
): string[] {
  if (!categoryAttributes) return [];

  const titleKeys = sortCategoryDefinitionKeys(categoryAttributes).filter((key) => {
    const def = categoryAttributes[key];
    return Boolean(def?.use_in_title) && !def?.secret_detail && !def?.disabled;
  });

  return titleKeys
    .map((key) => formatAttributeValueForTitle(objectAttributes[key], categoryAttributes[key]))
    .filter(Boolean);
}

export function buildProviderFoundObjectTitle(
  categoryName: string,
  objectAttributes: Record<string, unknown>,
  categoryAttributes?: Record<string, CategoryAttribute> | null
): string {
  const parts: string[] = ['Trouvé'];
  const categoryPart = sanitizeTitlePart(categoryName);
  if (categoryPart) parts.push(categoryPart);

  const markedParts = collectTitleAttributeParts(objectAttributes, categoryAttributes);
  if (markedParts.length > 0) {
    parts.push(...markedParts);
    return clampAnnouncementTitle(parts.join(' - '));
  }

  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return sanitizeTitlePart(value);
    if (typeof value === 'number' || typeof value === 'boolean') return sanitizeTitlePart(String(value));
    return '';
  };

  const attrs = objectAttributes;
  const explicitType =
    normalizeText(attrs.type) ||
    normalizeText(attrs.object_type) ||
    normalizeText(attrs.item_type);
  const brandValue = normalizeText(attrs.brand) || normalizeText(attrs.marque);
  const modelValue = normalizeText(attrs.model) || normalizeText(attrs.modele);
  const colorValue =
    normalizeText(attrs.color) ||
    normalizeText(attrs.colour) ||
    normalizeText(attrs.couleur) ||
    normalizeText(attrs.colors) ||
    normalizeText(attrs.color_name);

  if (explicitType) parts.push(explicitType);
  if (brandValue) parts.push(brandValue);
  if (modelValue) parts.push(modelValue);
  if (colorValue) parts.push(colorValue);

  return clampAnnouncementTitle(parts.join(' - '));
}
